<template>
  <div>
    <load-profile />
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>
    <b-card
      no-body
      class="mb-0 py-2"
    >
      <b-card-text class="mb-2 d-flex justify-content-between">
        <b-col>
          <label>{{ $t('general.entries') }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          >
            <template #no-options>
              {{ $t("selection.noOption") }}
            </template>
          </v-select>
        </b-col>
        <div>
          <b-button
            class="mr-1 py-1"
            size="sm"
            variant="primary"
            @click="handleCreateGroup"
          >
            {{ $t('button.addChatAgent') }}
          </b-button>
        </div>
      </b-card-text>

      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
        >
          <template #cell(name)="data">
            {{ getNameByLang(data.item) }}
          </template>

          <template #cell(isActive)="data">
            <b-badge
              v-if="data.item.isEnabled"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`status.active`) }}
            </b-badge>
            <b-badge
              v-else-if="!data.item.isEnabled"
              pill
              variant="danger"
              badge-glow
            >
              {{ $t(`status.inactive`) }}
            </b-badge>
          </template>

          <template #cell(serviceSystem)="data">
            <b-badge
              v-if="data.item.isServiceSystemEnabled"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`field.enabled`) }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="danger"
              badge-glow
            >
              {{ $t(`field.disabled`) }}
            </b-badge>
            <!-- <div v-if="data.item.isServiceSystemEnabled" class="mt-1">
              <small class="d-block">
                {{ $t('breadcrumb.project') }}:
                <span v-for="(project, index) in data.item.projects" :key="project.id">
                  {{ project.name }}
                </span>
              </small>
              <small class="d-block">
                {{ $t('field.category') }}: {{ data.item.serviceCategory }}
              </small>
              <small class="d-block">
                {{ $t('field.type') }}: {{ data.item.serviceType }}
              </small>
            </div> -->
          </template>
          <template #cell(task)="data">
            <span
              class="d-block text-truncate"
              style="max-width: 450px"
              v-html="getAbilityByLang(data.item).replace(/\n/g, '<br />')"
            />
          </template>
          <template #cell(totalWorkflowStep)="data">
            {{ data.item.steps.length }}
          </template>
          <template #cell(actions)="data">
            <div class="text-nowrap">
              <!--Detail Button-->
              <b-button
                v-b-tooltip.hover
                :title="$t('field.detail')"
                variant="info"
                pill
                size="sm"
                @click="handleClickDetail(data.item)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                v-if="$can('update', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.update')"
                variant="primary"
                pill
                size="sm"
                @click="handleUpdate(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="$can('delete', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.delete')"
                variant="danger"
                pill
                size="sm"
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </n-table>
        <!-- Pagination -->
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="getData"
        />
      </div>
    </b-card>

    <!-- Create & edit Modal -->
    <form-modal
      ref="createModal"
      :show="showFormModal"
      :edit-data="selectedData"
      @hide-modal="showFormModal = false"
      @created="onAgentCreated"
    />

    <!-- Edit Step Modal -->
    <edit-step-form
      ref="editStepModal"
      :show="showEditStepModal"
      :agent-data="selectedData"
      :step-index="selectedStepIndex"
      @hide-modal="showEditStepModal = false"
    />

    <!-- Detail Modal -->
    <detail-modal
      ref="detailModal"
      :show="showDetailModal"
      :agent-data="selectedData"
      @hide-modal="showDetailModal = false"
    />
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BButton,
  BBadge,
  BCol,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination.vue'
import NTable from '@/components/NTable.vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import FormModal from './FormModal.vue'
import EditStepForm from './EditStepForm.vue'
import tableField from './tableField'
import SearchInputs from './searchInput'
import DetailModal from './DetailModal.vue'

const ChatAgentRegistry = Repository.get('chatAgent')

export default {
  name: 'ChatAgent',

  components: {
    BCard,
    BCardText,
    BButton,
    BBadge,
    BCol,
    vSelect,
    NPagination,
    NTable,
    NSearchContainer,
    NSearchInput,
    FormModal,
    EditStepForm,
    DetailModal,
  },

  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      data: {
        name: null,
        ability: {
          en: '',
          vi: '',
          km: '',
          zh: '',
        },
        enabledServiceSystem: true,
        hookUrl: null,
        apiKey: null,
        steps: [
          {
            name: '',
          },
        ],
        projects: [],
        serviceCategoryCode: null,
        serviceTypeCode: null,
        servicePriceType: 1,
        servicePriceCurrency: 'CNY',
        servicePriceMin: null,
        servicePriceMax: null,
      },
      items: [],
      loading: false,
      query: {
        page: Number(this.$route.query.page) || 1,
        limit: Number(this.$route.query.limit) || 25,
      },
      showFormModal: false,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      perPage: this.$store.state.pagination.perPage,
      total: 0,
      selectedData: {},
      showEditStepModal: false,
      selectedStepIndex: 0,
      params: {
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        isEnabled: this.$route.query.isEnabled || null,
      },
      showDetailModal: false,
    }
  },

  computed: {
    fieldsGroupComputed() {
      return this.fieldsGroup.map(e => {
        if (e.key === 'localeCode') {
          e.options = this.locales
        }
        return e
      })
    },
  },

  watch: {
    perPage() {
      this.getData()
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    getData() {
      this.loading = true
      ChatAgentRegistry.list({
        ...this.query,
        perPage: this.perPage,
      })
        .then(response => {
          const data = response.data.data.list
          this.items = data.map(item => ({
            ...item,
            isExpanded: false,
            steps: item.steps || [],
            projects: item.projects || [],
            serviceCategoryName: item.serviceCategory?.translations?.[this.$i18n.locale] ||
              item.serviceCategory?.translations?.en || '',
            serviceTypeName: item.serviceType?.translations?.[this.$i18n.locale] ||
              item.serviceType?.translations?.en || '',
          }))
          this.total = response.data.data.total
        })
        .catch(() => {
          this.$toast.error(this.$t('alert.errorOccurred'))
        })
        .finally(() => {
          this.loading = false
        })
    },

    toggleExpanded(index) {
      this.$set(
        this.items[index],
        'isExpanded',
        !this.items[index].isExpanded,
      )
    },

    onShow(index) {
      this.$set(this.items[index], 'isExpanded', true)
    },

    onHide(index) {
      this.$set(this.items[index], 'isExpanded', false)
    },

    onAgentCreated() {
      this.getData()
    },

    onPageChange(page) {
      this.query.page = page
      this.getData()
    },

    handleCreateGroup() {
      this.selectedData = {}
      console.log('handleCreateGroup', this.selectedData)
      this.showFormModal = true
    },

    handleUpdate(data) {
      this.selectedData = data
      this.showFormModal = true
    },

    handleUpdateStep(agentData, index) {
      this.selectedData = agentData
      this.selectedStepIndex = index
      this.showEditStepModal = true
    },

    handleDelete(data) {
      this.confirmDelete(data.id)
    },

    destroyStep(stepId, stepIndex) {
      console.log('Delete step:', stepId, 'at index:', stepIndex)
    },

    confirmDelete(id) {
      const foundItem = this.items.find(item => item.id === id)
      const itemName = this.getNameByLang(foundItem)

      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.unableToRevertAfterDelete', {
            item: itemName || '',
          }),
          {
            title: `${this.$t('button.confirm')}!`,
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'primary',
            okTitle: this.$t('button.delete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.deleteAgent(id)
          }
        })
    },

    deleteAgent(id) {
      this.loading = true
      ChatAgentRegistry.delete(id)
        .then(() => {
          this.getData()
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },

    getParamName(param, parameters) {
      return parameters
        .flatMap(step => step.parameters)
        .find(p => p.key === param)?.name
    },

    getNameByLang(data) {
      if (!data?.name) return ''

      return data.name?.[this.$i18n.locale] || data.name?.zh || data.name?.en || data.name?.km || data.name?.vi || ''
    },

    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },

    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },

    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },

    handleClickDetail(agent) {
      this.selectedData = agent
      this.showDetailModal = true
    },

    getAbilityByLang(data) {
      if (!data?.ability) return ''

      return data.ability?.[this.$i18n.locale] || data.ability?.zh || data.ability?.en || data.ability?.km || data.ability?.vi || ''
    },
  },

  setup() {
    const fieldsCommand = []
    const fieldsGroup = []
    const permissionType = 'call-center'
    const fields = tableField
    const route = 'agent'
    const searchFields = SearchInputs

    return {
      fieldsCommand,
      fieldsGroup,
      permissionType,
      fields,
      route,
      searchFields,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.link-hover:hover {
  text-decoration: underline;
}
</style>
